import { createAsyncThunk } from "@reduxjs/toolkit";
import { AxiosInstance } from "axios";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { ApiErrorResponse } from "../../../api/types/ApiErrorResponse";
import { toApiError } from "../../../api/utils/errorHandling";
import { contentDispositionFileName } from "../utils/contentDispositionFileName";

/** The member's training record (Ausbildungsnachweis) per enrolment, as a PDF download (US-8). */
type Rejection = { rejectValue: ApiErrorResponse };

/**
 * Downloads the enrolment's training record PDF. The file name comes from the server's
 * Content-Disposition (class and date, never the member's name). A 409 means the school
 * has no business details yet.
 */
export const downloadTrainingRecord = createAsyncThunk<
  string,
  { memberId: string; enrolmentId: string },
  Rejection
>("training/downloadRecord", async ({ memberId, enrolmentId }, { rejectWithValue }) => {
  try {
    return await downloadPdf(`/training/members/${memberId}/enrolments/${enrolmentId}/record`);
  } catch (error: unknown) {
    return rejectWithValue(await toBlobApiError(error));
  }
});

/**
 * Downloads the Ausbildungsnachweis of a record kept under legal hold after its learner was
 * erased (PD-0021). Owner/Admin only; the file name again carries class and date only.
 */
export const downloadHeldTrainingRecord = createAsyncThunk<string, string, Rejection>(
  "training/downloadHeldRecord",
  async (enrolmentId, { rejectWithValue }) => {
    try {
      return await downloadPdf(`/training/held-records/${enrolmentId}/record.pdf`);
    } catch (error: unknown) {
      return rejectWithValue(await toBlobApiError(error));
    }
  }
);

/**
 * Fetches the PDF and offers it as a download under the server's Content-Disposition name.
 * The portal passes its own axios instance (portal token); staff screens use the default.
 */
export const downloadPdf = async (path: string, client: AxiosInstance = axiosInstance): Promise<string> => {
  const response = await client.get<Blob>(path, { responseType: "blob" });
  const disposition = response.headers?.["content-disposition"] as string | undefined;
  const fileName = contentDispositionFileName(disposition) ?? "training-record.pdf";

  const url = URL.createObjectURL(response.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return fileName;
};

// A blob-typed request wraps the error JSON in a Blob; unwrap it so the message survives.
export const toBlobApiError = async (error: unknown): Promise<ApiErrorResponse> => {
  const maybeAxios = error as { response?: { data?: unknown } };
  const data = maybeAxios.response?.data;
  if (data instanceof Blob && data.type.includes("json")) {
    try {
      maybeAxios.response!.data = JSON.parse(await data.text());
    } catch {
      // fall through to the generic mapping
    }
  }
  return toApiError(error, "Failed to download the training record");
};
